import { useEffect, useMemo, useState } from "react";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { fetchExpenses } from "services/expenseService";
import { getProfitLoss } from "services/finance.service";
import {
  EXPENSE_CATEGORY_META,
  EXPENSE_CATEGORY_OPTIONS,
} from "constants/expenseConstants";

const CATEGORY_META = EXPENSE_CATEGORY_META;
const COLORS = CATEGORY_META.map((c) => c.color);

const formatCurrency = (value) =>
  `$${Number(value ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const isCustomRangeIncomplete = (filters) =>
  filters?.dateRangeType === "custom" &&
  (!filters?.customDateRange?.[0] || !filters?.customDateRange?.[1]);

const ChartSkeleton = ({ height = 280 }) => (
  <div className="animate-pulse bg-gray-200 rounded" style={{ height }} />
);

const EXPENSE_TRENDS_EMPTY_MESSAGE =
  "Add your first manual expense to start tracking trends.";

const EmptyChart = ({ message, height = 280 }) => (
  <div
    className="flex items-center justify-center text-sm text-gray-500 italic text-center px-4"
    style={{ height }}
  >
    {message}
  </div>
);

const BreakdownTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  return (
    <div className="bg-white p-3 border border-gray-200 shadow-sm rounded-md text-sm">
      <p className="font-medium text-gray-900">{entry.name}</p>
      <p style={{ color: entry.payload?.fill || entry.color }}>
        {formatCurrency(entry.value)}
      </p>
    </div>
  );
};

export default function ExpenseAnalytics({ api, filters, financeMode = false }) {
  const [categoryFilter, setCategoryFilter] = useState(undefined);
  const [expenses, setExpenses] = useState([]);
  const [profitLoss, setProfitLoss] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setExpenses([]);
      setProfitLoss(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [expenseRes, plRes] = await Promise.all([
          fetchExpenses(api, { filters }),
          getProfitLoss(api, { filters }),
        ]);
        if (cancelled) return;
        setExpenses(expenseRes?.records ?? []);
        setProfitLoss(plRes);
      } catch {
        if (!cancelled) {
          setExpenses([]);
          setProfitLoss(null);
          setError("Failed to load expense analytics.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [api, filters?.dateRangeType, filters?.customDateRange?.[0], filters?.customDateRange?.[1]]);

  const filteredExpenses = useMemo(
    () =>
      expenses.filter((e) => {
        if (e.isDeleted) return false;
        if (categoryFilter && e.category !== categoryFilter) return false;
        return true;
      }),
    [expenses, categoryFilter]
  );

  const breakdownData = useMemo(() => {
    const totals = { team_current: 0, other_costs: 0, subscriptions: 0 };
    filteredExpenses.forEach((e) => {
      if (totals[e.category] !== undefined) totals[e.category] += e.amount ?? 0;
    });
    return CATEGORY_META.map(({ key, label, color }) => ({
      name: label,
      value: totals[key],
      fill: color,
    })).filter((d) => d.value > 0);
  }, [filteredExpenses]);

  const realProfit = profitLoss?.profit ?? 0;
  const chartHeight = 280;

  const renderChartShell = (height = chartHeight) => {
    if (isLoading) return <ChartSkeleton height={height} />;
    if (isCustomRangeIncomplete(filters)) {
      return <EmptyChart message="Select start and end dates." height={height} />;
    }
    return null;
  };

  return (
    <section className={financeMode ? "mb-8" : "mb-8"}>
      {financeMode && (
        <h2 className="text-lg font-medium text-gray-900 mb-4">Expense Analytics</h2>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => setCategoryFilter(undefined)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
            !categoryFilter
              ? "bg-indigo-600 text-white border-indigo-600"
              : "bg-white text-gray-600 border-gray-300 hover:border-indigo-300"
          }`}
        >
          All Categories
        </button>
        {EXPENSE_CATEGORY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setCategoryFilter(opt.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              categoryFilter === opt.value
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-gray-600 border-gray-300 hover:border-indigo-300"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Expense Breakdown</h3>
          {renderChartShell() ||
            (breakdownData.length === 0 ? (
              <EmptyChart message={EXPENSE_TRENDS_EMPTY_MESSAGE} height={chartHeight} />
            ) : (
              <ResponsiveContainer width="100%" height={chartHeight}>
                <PieChart>
                  <Pie
                    data={breakdownData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                  >
                    {breakdownData.map((entry, i) => (
                      <Cell key={entry.name} fill={entry.fill || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<BreakdownTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ))}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-medium text-gray-900 mb-4">P&amp;L Snapshot</h3>
          {renderChartShell() || (
            <div className="grid grid-cols-2 gap-4" style={{ minHeight: chartHeight }}>
              {[
                { label: "Revenue", value: profitLoss?.revenue ?? 0, color: "text-emerald-600" },
                { label: "Expenses", value: profitLoss?.expenses ?? 0, color: "text-red-600" },
                {
                  label: "Commissions",
                  value: profitLoss?.commissions ?? 0,
                  color: "text-amber-600",
                },
                { label: "Net Profit", value: realProfit, color: "text-indigo-600" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-gray-100 bg-gray-50 p-4 flex flex-col justify-center"
                >
                  <p className="text-xs text-gray-500 uppercase tracking-wide">{item.label}</p>
                  <p className={`text-lg font-semibold mt-1 ${item.color}`}>
                    {formatCurrency(item.value)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
